/**
 * ポスター描画の共通部品
 *
 * html2canvas 等は導入していないため Canvas 2D で手描きする。
 * 画像は apiClient (Bearer 付き) で Blob 取得してから描くので canvas は汚染されない。
 */

import { apiClient } from '@/api/client'
import type { TripDay, TripDetail, TripRoutePoint } from '@/api/types/trip'

export const WIDTH = 1080
export const HEIGHT = 1350
export const MARGIN = 72

export const BG = '#0f1115'
export const DEFAULT_ACCENT = '#f97316'
export const TEXT = '#f8fafc'
export const SUBTLE = '#94a3b8'

export const FONT = (size: number, weight = 400) =>
  `${weight} ${size}px "Hiragino Sans", "Noto Sans JP", system-ui, sans-serif`

/** レイアウトに渡す描画材料 */
export type PosterInput = {
  trip: TripDetail
  routePoints: TripRoutePoint[]
  days: TripDay[]
  accent: string
  headline: string
  subcopy: string
  keywords: string[]
  dayLines: string[]
  hero: ImageBitmap | null
  collage: ImageBitmap[]
  filmstrip: ImageBitmap[]
}

/** Immich サムネイルを Bitmap として取得（失敗しても致命的ではないので null を返す） */
export const loadAssetBitmap = async (assetId: string, size: 'thumbnail' | 'preview' = 'preview') => {
  try {
    const res = await apiClient.get<Blob>(`/immich/thumbnail/${assetId}?size=${size}`, { responseType: 'blob' })
    return await createImageBitmap(res.data)
  } catch {
    return null
  }
}

/** 角丸矩形パス */
export const roundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/** 短辺基準で中央トリミングして描画 */
export const drawCover = (
  ctx: CanvasRenderingContext2D,
  bitmap: ImageBitmap,
  x: number,
  y: number,
  w: number,
  h: number,
) => {
  const scale = Math.max(w / bitmap.width, h / bitmap.height)
  const sw = w / scale
  const sh = h / scale
  ctx.drawImage(bitmap, (bitmap.width - sw) / 2, (bitmap.height - sh) / 2, sw, sh, x, y, w, h)
}

/** 角丸クリップした状態で cover 描画 */
export const drawRoundedCover = (
  ctx: CanvasRenderingContext2D,
  bitmap: ImageBitmap,
  x: number,
  y: number,
  w: number,
  h: number,
  r = 20,
) => {
  ctx.save()
  roundedRect(ctx, x, y, w, h, r)
  ctx.clip()
  drawCover(ctx, bitmap, x, y, w, h)
  ctx.restore()
}

/** 日本語は単語境界が無いので 1 文字ずつ詰めて折り返す */
export const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number) => {
  const lines: string[] = []
  let line = ''
  for (const char of text) {
    const next = line + char
    if (line !== '' && ctx.measureText(next).width > maxWidth) {
      lines.push(line)
      if (lines.length === maxLines) return lines
      line = char
      continue
    }
    line = next
  }
  if (line !== '') lines.push(line)
  return lines
}

/** 指定行数に収まる最大のフォントサイズを探して行に分割する */
export const fitLines = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
  maxSize: number,
  minSize: number,
  weight = 700,
) => {
  for (let size = maxSize; size > minSize; size -= 4) {
    ctx.font = FONT(size, weight)
    const lines = wrapText(ctx, text, maxWidth, maxLines + 1)
    if (lines.length <= maxLines) return { size, lines }
  }
  ctx.font = FONT(minSize, weight)
  return { size: minSize, lines: wrapText(ctx, text, maxWidth, maxLines) }
}

/** #RRGGBB に不透明度を足す */
export const withAlpha = (hex: string, alpha: number) => {
  const normalized = /^#[0-9a-f]{6}$/i.test(hex) ? hex : DEFAULT_ACCENT
  const value = Number.parseInt(normalized.slice(1), 16)
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`
}

/** #RRGGBB を [r, g, b] に分解する */
const toRgb = (hex: string): [number, number, number] => {
  const normalized = /^#[0-9a-f]{6}$/i.test(hex) ? hex : DEFAULT_ACCENT
  const value = Number.parseInt(normalized.slice(1), 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

/** 階調化に使う中間解像度（そのままだと 145 万画素の走査になるので落として拡大する） */
const DUOTONE_SCALE = 0.5
/** 明度の段数。少ないほどリソグラフ寄りになる */
const DUOTONE_LEVELS = 5

/**
 * 写真を明度で階調化し、暗色とアクセント色の 2 色刷り風に置き換える
 *
 * 画像生成モデルが使えない環境でもアートワーク面を成立させるための代替。
 */
export const drawDuotone = (ctx: CanvasRenderingContext2D, bitmap: ImageBitmap, accent: string) => {
  const w = Math.round(WIDTH * DUOTONE_SCALE)
  const h = Math.round(HEIGHT * DUOTONE_SCALE)
  const offscreen = document.createElement('canvas')
  offscreen.width = w
  offscreen.height = h
  const offCtx = offscreen.getContext('2d', { willReadFrequently: true })
  if (!offCtx) return

  drawCover(offCtx, bitmap, 0, 0, w, h)
  const image = offCtx.getImageData(0, 0, w, h)
  const data = image.data
  const [ar, ag, ab] = toRgb(accent)
  const [dr, dg, db] = [10, 12, 18]
  const steps = DUOTONE_LEVELS - 1

  for (let i = 0; i < data.length; i += 4) {
    const [r, g, b] = data.subarray(i, i + 3)
    const level = Math.round(((r * 0.299 + g * 0.587 + b * 0.114) / 255) * steps) / steps
    data.set(
      [dr + (ar - dr) * level, dg + (ag - dg) * level, db + (ab - db) * level].map((v) => Math.round(v)),
      i,
    )
  }
  offCtx.putImageData(image, 0, 0)
  ctx.drawImage(offscreen, 0, 0, WIDTH, HEIGHT)
}

/** 上下いずれかから伸びる暗いグラデーションを重ねる */
export const drawScrim = (
  ctx: CanvasRenderingContext2D,
  from: number,
  to: number,
  startAlpha: number,
  endAlpha: number,
) => {
  const gradient = ctx.createLinearGradient(0, from, 0, to)
  gradient.addColorStop(0, `rgba(15, 17, 21, ${startAlpha})`)
  gradient.addColorStop(1, `rgba(15, 17, 21, ${endAlpha})`)
  ctx.fillStyle = gradient
  ctx.fillRect(0, Math.min(from, to), WIDTH, Math.abs(to - from))
}

export type Box = { x: number; y: number; w: number; h: number }

/** 緯度経度を矩形に等比で射影してルート線を描く */
export const drawRoute = (ctx: CanvasRenderingContext2D, points: TripRoutePoint[], box: Box, accent: string) => {
  if (points.length < 2) return

  const lats = points.map((p) => p.latitude)
  const lngs = points.map((p) => p.longitude)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)

  const spanLat = Math.max(maxLat - minLat, 1e-6)
  const spanLng = Math.max(maxLng - minLng, 1e-6)
  const scale = Math.min(box.w / spanLng, box.h / spanLat)
  const offsetX = box.x + (box.w - spanLng * scale) / 2
  const offsetY = box.y + (box.h - spanLat * scale) / 2

  const project = (p: TripRoutePoint) => ({
    x: offsetX + (p.longitude - minLng) * scale,
    // 緯度は上が大きいので反転
    y: offsetY + (maxLat - p.latitude) * scale,
  })

  ctx.save()
  ctx.strokeStyle = accent
  ctx.lineWidth = 4
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.shadowColor = withAlpha(accent, 0.6)
  ctx.shadowBlur = 18

  ctx.beginPath()
  points.forEach((point, i) => {
    const { x, y } = project(point)
    if (i === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  })
  ctx.stroke()
  ctx.shadowBlur = 0

  const first = points.at(0)
  const last = points.at(-1)
  if (first && last) {
    for (const [pos, color] of [
      [project(first), '#38bdf8'],
      [project(last), '#ef4444'],
    ] as const) {
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, 10, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
      ctx.lineWidth = 3
      ctx.strokeStyle = BG
      ctx.stroke()
    }
  }
  ctx.restore()
}

/** 数値ラベルを等幅の列に並べる */
export const drawStats = (
  ctx: CanvasRenderingContext2D,
  items: [string, string][],
  top: number,
  valueSize = 44,
  color = TEXT,
) => {
  if (items.length === 0) return
  const colWidth = (WIDTH - MARGIN * 2) / items.length
  items.forEach(([label, value], i) => {
    const cx = MARGIN + colWidth * i + colWidth / 2
    ctx.textAlign = 'center'
    ctx.fillStyle = color
    ctx.font = FONT(valueSize, 700)
    ctx.fillText(value, cx, top + valueSize)
    ctx.fillStyle = SUBTLE
    ctx.font = FONT(24)
    ctx.fillText(label, cx, top + valueSize + 40)
  })
  ctx.textAlign = 'left'
}

/** キーワードを丸ピルで横に並べる（描画後の右端 x を返す） */
export const drawKeywordPills = (
  ctx: CanvasRenderingContext2D,
  keywords: string[],
  x: number,
  baseline: number,
  accent: string,
) => {
  let cursorX = x
  ctx.font = FONT(24, 700)
  for (const keyword of keywords) {
    const w = ctx.measureText(keyword).width + 40
    roundedRect(ctx, cursorX, baseline - 30, w, 44, 22)
    ctx.fillStyle = withAlpha(accent, 0.18)
    ctx.fill()
    ctx.strokeStyle = withAlpha(accent, 0.7)
    ctx.lineWidth = 2
    ctx.stroke()
    ctx.fillStyle = accent
    ctx.fillText(keyword, cursorX + 20, baseline)
    cursorX += w + 12
  }
  return cursorX
}

/** ポスター共通の統計 4 項目 */
export const statItems = (trip: TripDetail): [string, string][] => [
  ['移動距離', `${Math.round(trip.stats.travel_distance_km).toLocaleString()} km`],
  ['歩数', trip.stats.total_steps.toLocaleString()],
  ['スポット', `${trip.stats.place_count}`],
  ['写真', `${trip.stats.photo_count}`],
]
